/**
 * SEO URL Generator Utility
 * 
 * This file contains functions to generate SEO-friendly URLs for various components
 * based on the facet_meta API data and new URL patterns.
 */

import { URL_TYPES } from '../constants/seoUrlPatterns';

const legacyProductGroupUrl = (productGroupId, cookie) =>
  `product/${productGroupId}?pgId=${productGroupId}&page=1&per_page=12&sort_by=landed_cpr${cookie ? `&zip=${cookie}` : ""}`;

const pathFromUrl = (input) => {
  if (!input) return null;

  try {
    if (/^https?:\/\//i.test(input)) {
      return new URL(input).pathname;
    }
  } catch (error) {
    return null;
  }

  return input.startsWith("/") ? input : `/${input}`;
};

const slugify = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const productSlugPath = (productData) => {
  const existingPath = pathFromUrl(productData?.seo_url);
  if (existingPath) return existingPath;

  const titleSlug = slugify(productData?.title);
  const productGroupId = productData?.product_group_id;

  return titleSlug && productGroupId ? `/${titleSlug}-${productGroupId}` : null;
};

const weaponCategorySlug = (category, fallback) => {
  const categoryMap = {
    'Handgun Ammo': 'handgun-ammo',
    'Rifle Ammo': 'rifle-ammo',
    'Rimfire Ammo': 'rimfire-ammo',
    'Shotgun Ammo': 'shotgun-ammo',
    Handgun: 'handgun-ammo',
    Rifle: 'rifle-ammo',
    Rimfire: 'rimfire-ammo',
    Shotgun: 'shotgun-ammo',
  };

  const slug = categoryMap[category] || fallback || slugify(category);

  return slug.endsWith("-ammo") ? slug : `${slug}-ammo`;
};

/**
 * Generates SEO URL for product group links
 * @param {Object} productData - Product data object
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @param {string} cookie - User zip cookie
 * @returns {string} - SEO URL for product group
 */
export const generateProductSeoUrl = (productData, facetMappings, cookie) => {
  const seoPath = productSlugPath(productData);
  if (seoPath) return seoPath;

  if (!productData || !facetMappings) {
    // Fallback to old URL format
    return legacyProductGroupUrl(productData?.product_group_id, cookie);
  }

  try {
    const { weapon_category, caliber, brand } = productData;
    
    // Get SEO slugs from facet mappings using label-based mappings
    const categorySlug = weaponCategorySlug(
      weapon_category,
      facetMappings.weapon_category_label?.[weapon_category],
    );
    const caliberSlug = facetMappings.caliber_label?.[caliber] || slugify(caliber);
    const brandSlug = facetMappings.brand_label?.[brand] || slugify(brand);

    // Build SEO URL: /pd/{weapon_category}-ammo/{caliber}/{brand}
    let seoUrl = `/${URL_TYPES.PRODUCTS}/${categorySlug}`;
    
    if (caliberSlug) {
      seoUrl += `/${caliberSlug}`;
    }
    
    if (brandSlug) {
      seoUrl += `/${brandSlug}`;
    }

    return seoUrl;
  } catch (error) {
    console.error('Error generating product SEO URL:', error);
    // Fallback to old URL format
    return legacyProductGroupUrl(productData?.product_group_id, cookie);
  }
};

/**
 * Generates SEO URL for header navigation links (without /ls/ prefix)
 * @param {string} category - Category name
 * @param {string} name - Subcategory/caliber name
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @param {string} cookie - User zip cookie
 * @returns {string} - SEO URL for header link
 */
export const generateHeaderSeoUrl = (category, name, facetMappings) => {
  if (!facetMappings) {
    // Fallback to old URL format
    return `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(category)}/${slugify(name)}`;
  }

  try {
    // Get caliber slug from facet mappings using label-based mappings
    const caliberSlug = facetMappings.caliber_label?.[name] || slugify(name);

    // Build canonical URL: /pd/{weapon_category}-ammo/{caliber}
    let seoUrl = `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(category)}`;
    
    if (caliberSlug) {
      seoUrl += `/${caliberSlug}`;
    }

    return seoUrl;
  } catch (error) {
    console.error('Error generating header SEO URL:', error);
    // Fallback to old URL format
    return `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(category)}/${slugify(name)}`;
  }
};

/**
 * Generates SEO URL for footer category links
 * @param {string} categoryName - Category name
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @returns {string} - SEO URL for category
 */
export const generateFooterCategorySeoUrl = (categoryName, facetMappings) => {
  if (!facetMappings) {
    // Fallback to old URL format
    return `/${categoryName.toLowerCase().replace(/\s+/g, '-')}`;
  }

  try {
    // Map category name to weapon category
    const categoryMap = {
      'Handgun Ammo': 'handgun-ammo',
      'Rifle Ammo': 'rifle-ammo',
      'Rimfire Ammo': 'rimfire-ammo', 
      'Shotgun Ammo': 'shotgun-ammo'
    };

    const categorySlug = categoryMap[categoryName] || slugify(categoryName);

    // Build SEO URL: /{weapon_category}-ammo
    return `/${categorySlug}`;
  } catch (error) {
    console.error('Error generating footer category SEO URL:', error);
    // Fallback to old URL format
    return `/${categoryName.toLowerCase().replace(/\s+/g, '-')}`;
  }
};

/**
 * Generates SEO URL for brand links
 * @param {string} brandName - Brand name
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @param {string} cookie - User zip cookie
 * @returns {string} - SEO URL for brand
 */
export const generateBrandSeoUrl = (brandName, facetMappings, cookie) => {
  if (!facetMappings) {
    // Fallback to old URL format
    return `/${brandName.toLowerCase().replace(/\s+/g, '-')}${cookie ? `?zip=${cookie}` : ""}`;
  }

  try {
    // Get brand slug from facet mappings using label-based mappings
    const brandSlug = facetMappings.brand_label?.[brandName] || brandName?.toLowerCase().replace(/\s+/g, '-');

    // Build SEO URL: /brands/{brand}
    let seoUrl = `/${URL_TYPES.BRANDS}/${brandSlug}`;

    // Add query parameters
    const queryParams = new URLSearchParams();
    
    if (cookie) {
      queryParams.set('zip', cookie);
    }

    return queryParams.toString() ? `${seoUrl}?${queryParams.toString()}` : seoUrl;
  } catch (error) {
    console.error('Error generating brand SEO URL:', error);
    // Fallback to old URL format
    return `/${brandName.toLowerCase().replace(/\s+/g, '-')}${cookie ? `?zip=${cookie}` : ""}`;
  }
};

/**
 * Generates SEO URL for caliber links
 * @param {string} caliberName - Caliber name
 * @param {string} weaponCategory - Weapon category
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @param {string} cookie - User zip cookie
 * @returns {string} - SEO URL for caliber
 */
export const generateCaliberSeoUrl = (caliberName, weaponCategory, facetMappings) => {
  if (!facetMappings) {
    // Fallback to old URL format
    return `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(weaponCategory)}/${slugify(caliberName)}`;
  }

  try {
    // Get caliber slug from facet mappings using label-based mappings
    const caliberSlug = facetMappings.caliber_label?.[caliberName] || slugify(caliberName);

    return `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(weaponCategory)}/${caliberSlug}`;
  } catch (error) {
    console.error('Error generating caliber SEO URL:', error);
    // Fallback to old URL format
    return `/${URL_TYPES.PRODUCTS}/${weaponCategorySlug(weaponCategory)}/${slugify(caliberName)}`;
  }
};

/**
 * Generates SEO URL for breadcrumb links
 * @param {Array} pathSegments - Array of path segments
 * @param {Object} facetMappings - Facet mappings from facet_meta API
 * @returns {string} - SEO URL for breadcrumb
 */
export const generateBreadcrumbSeoUrl = (pathSegments, facetMappings) => {
  if (!facetMappings || !pathSegments || pathSegments.length === 0) {
    // Fallback to old URL format
    return `/${pathSegments.join('/')}`;
  }

  try {
    // Build path from segments
    let seoUrl = '';
    
    for (let i = 0; i < pathSegments.length; i++) {
      const segment = pathSegments[i];
      
      // Check if this is a known URL type prefix
      if (Object.values(URL_TYPES).includes(segment)) {
        seoUrl += `/${segment}`;
        continue;
      }
      
      // For other segments, try to map them using facet mappings
      let mappedSegment = segment;
      
      // Try to find mapping for this segment using reverse mappings
      if (facetMappings.weapon_category_label_reverse) {
        if (facetMappings.weapon_category_label_reverse[segment]) {
          mappedSegment = segment;
        }
      }
      
      if (facetMappings.caliber_label_reverse) {
        if (facetMappings.caliber_label_reverse[segment]) {
          mappedSegment = segment;
        }
      }
      
      if (facetMappings.brand_label_reverse) {
        if (facetMappings.brand_label_reverse[segment]) {
          mappedSegment = segment;
        }
      }
      
      seoUrl += `/${mappedSegment}`;
    }
    
    return seoUrl || '/';
  } catch (error) {
    console.error('Error generating breadcrumb SEO URL:', error);
    // Fallback to old URL format
    return `/${pathSegments.join('/')}`;
  }
};
